import * as React from "react";
import { LineItemModels, LineItemMappers, LineItemSlotsProvider, LineItemCard, DeliveryEstimation } from "@grandvisionhq/lib-visuals";
import { useIntl, useFeatureToggles } from "@grandvisionhq/state";
import { useRemoveInsuranceFromCart } from "../../../../../../hooks/hooks/use-remove-insurance-from-cart.js";
import { Insurance } from "../../../../components/modules/LineItems/components/Insurance.js";
import { QuantitySelector } from "../../../../components/modules/LineItems/components/QuantitySelector.js";
import { useCartSlots } from "../../../../../../slots/slots/context.js";
import { PriceTooltipContainer } from "../../../../containers/modules/LineItems/containers/PriceTooltipContainer.js";
import { DeliveryIndicatorContainer } from "../../../../containers/modules/LineItems/containers/DeliveryIndicatorContainer.js";
const VisualLineItemsContainer = ({ lineItems, isReadOnly }) => {
  const intl = useIntl();
  const {
    isLineItemDeliveryEstimationDisabled,
    isPrescriptionGlassesQuantitySelectorDisabled = false,
    isPrescriptionGlassesDuplicateButtonDisabled = true,
    isDynamicLineItemDeliveryEstimationEnabled = false
  } = useFeatureToggles();
  const { Slots } = useCartSlots();
  const [removeFromCart] = useRemoveInsuranceFromCart();
  const [isProtectionModalOpen, setIsProtectionModalOpen] = React.useState(false);
  const handleProtectionInfoToggle = React.useCallback(() => {
    setIsProtectionModalOpen(!isProtectionModalOpen);
  }, [isProtectionModalOpen]);
  const labels = React.useMemo(
    () => ({
      quantity: intl.getLabel("cart.item.quantity"),
      iconLeftEye: intl.getLabel("products.contactLenses.leftEye"),
      iconRightEye: intl.getLabel("products.contactLenses.rightEye"),
      power: intl.getLabel("products.contactLenses.prescription.power"),
      deliveredEvery: intl.getLabel("cart.delivery.frequency"),
      perBox: intl.getLabel("products.perBox"),
      configuration: intl.getLabel("account.order.detail.configuration"),
      prescription: intl.getLabel("account.order.detail.prescription"),
      subscriptionDetails: intl.getLabel("cart.item.subscription.title"),
      addOn: intl.getLabel("products.addOn.title"),
      removeInsurance: intl.getLabel("products.insurance.remove"),
      delivery: intl.getLabel("products.deliveryEstimation.label"),
      deliveryTooltip: intl.getLabel("cart.lineItem.deliveryEstimation.tooltip", {
        hideLabelKey: true
      }),
      disclaimer: intl.getLabel("cart.lineItem.glasses.disclaimer", { hideLabelKey: true }),
      disclaimerTooltip: intl.getLabel("cart.lineItem.glasses.disclaimerTooltip", {
        hideLabelKey: true
      })
    }),
    [intl]
  );
  const removeInsurance = (insurance) => {
    if (!insurance) {
      return;
    }
    removeFromCart(insurance.id);
  };
  const handleRemoval = (lineItem) => removeInsurance(lineItem.insurance);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, Slots.ProtectionInfoModal && /* @__PURE__ */ React.createElement(
    Slots.ProtectionInfoModal,
    {
      isOpen: isProtectionModalOpen,
      onClose: handleProtectionInfoToggle
    }
  ), lineItems.map((lineItem, i) => {
    const showInsurance = !lineItem.insurance && lineItem.eligibleInsurance && lineItem.eligibleInsurance.sku && !isReadOnly;
    const accordionSection = [];
    if ("prescription" in lineItem) {
      if (LineItemModels.isGlassesBundleLineItem(lineItem)) {
        accordionSection.push({
          type: "GLASSES_BUNDLE",
          title: labels.prescription,
          prescription: lineItem.prescription
        });
      } else if (lineItem.prescription) {
        accordionSection.push({
          type: "CONTACT_LENSES",
          title: labels.prescription,
          prescription: lineItem.prescription
        });
      }
    }
    if (lineItem.isSubscription && lineItem.subscriptionDetails?.deliveryInterval) {
      accordionSection.push({
        type: "SUBSCRIPTION",
        title: labels.subscriptionDetails,
        expanded: true,
        deliveryInterval: lineItem.subscriptionDetails.deliveryInterval
      });
    }
    if ("bundleChildLineItems" in lineItem && lineItem.bundleChildLineItems) {
      const bundleChildLineItems = LineItemMappers.getEnrichedBundleChildLineItems({
        lineItem,
        addOnLabel: labels.addOn,
        isReadOnly,
        actionLabel: labels.removeInsurance,
        actionCallback: handleRemoval
      });
      accordionSection.push({
        type: "GENERIC_BUNDLE",
        title: labels.configuration,
        bundleChildLineItems,
        expanded: Boolean(lineItem.insurance?.sku),
        ...labels.disclaimer && { disclaimer: labels.disclaimer },
        ...labels.disclaimerTooltip && { disclaimerTooltip: labels.disclaimerTooltip }
      });
    }
    const baseProps = {
      accordionSection,
      currency: intl.currency,
      originalPrice: lineItem.originalPrice,
      discount: lineItem.discountPrice,
      relativeDiscount: lineItem.relativeDiscount,
      imagePath: lineItem.imageUrl,
      lineItemId: lineItem.lineItemId,
      price: lineItem.price ?? 0,
      quantity: lineItem.quantity,
      subtitle: lineItem.subtitle,
      title: lineItem.title,
      isCompactCard: false,
      hasDiscountSection: true,
      quoteConfig: lineItem?.quoteConfig,
      additionalInfo: lineItem.additionalInfo,
      isContainerBasedLayout: true
    };
    const showQuantity = () => {
      return !(lineItem.type === "GLASSES_BUNDLE" && isPrescriptionGlassesQuantitySelectorDisabled);
    };
    const LineItemCardSlot = {
      QuantitySelector: !isReadOnly ? () => /* @__PURE__ */ React.createElement(
        QuantitySelector,
        {
          shouldShowQuantity: showQuantity(),
          shouldShowDuplicateButton: lineItem.type === "GLASSES_BUNDLE" && !isPrescriptionGlassesDuplicateButtonDisabled,
          lineItemId: lineItem.lineItemId,
          isReadOnly
        }
      ) : void 0,
      StockAndDelivery: !isLineItemDeliveryEstimationDisabled ? () => /* @__PURE__ */ React.createElement(DeliveryIndicatorContainer, { lineItemId: lineItem.lineItemId }) : void 0,
      PriceTooltip: () => lineItem.unitPrice?.appliedDiscounts?.length ? /* @__PURE__ */ React.createElement(
        PriceTooltipContainer,
        {
          id: lineItem.id,
          appliedDiscounts: lineItem.unitPrice.appliedDiscounts
        }
      ) : null,
      Footer: () => showInsurance ? /* @__PURE__ */ React.createElement(Insurance, { visualLineItem: lineItem, handleOpenModal: handleProtectionInfoToggle }) : null,
      DeliveryEstimation: isDynamicLineItemDeliveryEstimationEnabled ? () => /* @__PURE__ */ React.createElement(
        DeliveryEstimation,
        {
          tooltipId: `delivery-estimation-tooltip-${lineItem.sku}${i}`,
          labels: {
            label: labels.delivery,
            tooltip: labels.deliveryTooltip
          },
          earliestDeliveryDate: lineItem.deliveryEstimation?.earliestDeliveryDate,
          latestDeliveryDate: lineItem.deliveryEstimation?.latestDeliveryDate
        }
      ) : void 0
    };
    return /* @__PURE__ */ React.createElement(LineItemSlotsProvider, { key: `${lineItem.sku}-${i}`, slots: LineItemCardSlot }, /* @__PURE__ */ React.createElement(
      LineItemCard,
      {
        key: `${lineItem.sku}-${i}`,
        imageHref: lineItem.productUrl,
        ...baseProps
      }
    ));
  }));
};
export {
  VisualLineItemsContainer
};
//# sourceMappingURL=VisualLineItemsContainer.js.map
